"use client";
// ChartRenderer.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import "echarts/extension/dataTool";
import gaService from "@/api/services/ga.service";
import useDevice from "@/hooks/useDevice";

export interface ChartData {
  title: string;
  type:
    | "line"
    | "bar"
    | "bar-horizontal" // supported
    | "bar-stacked" // supported
    | "pie"
    | "donut"
    | "heatmap"
    | "radial" // treated like donut
    | "table"
    | "category";
  x?: { label: string; values: string[] };
  y?: { label: string; values: number[]; display_values?: string[] }[];
  values?: Record<string, unknown>[];
}

export type ColorScheme = "purple" | "blue";

type SchemeColor = {
  lineColors: string[];
  areaColors: [string, string];
  pieColors: string[];
  barColors: string[];
  barHoverColors: string[];
  stripe: string;
};

const schemeColors: Record<ColorScheme, SchemeColor> = {
  purple: {
    lineColors: ["#C29BFF", "#7720FF", "#FFB800"],
    areaColors: ["rgba(119,32,255,0.3)", "rgba(119,32,255,0)"],
    pieColors: ["#00fff6", "#7720FF", "#26D07C", "#FFB800"],
    barColors: ["#26D07C", "#FF7578", "#FFB800"],
    barHoverColors: ["#B688FF", "#7720FF", "#FFA500"],
    stripe: "#7720FF",
  },
  blue: {
    lineColors: ["#5AB3FF", "#0099FF", "#FFB800"],
    areaColors: ["rgba(0,153,255,0.3)", "rgba(0,153,255,0)"],
    pieColors: ["#4FC3F7", "#0288D1", "#26D07C", "#FF7578"],
    barColors: ["#51C796", "#FF7578", "#FFB800"],
    barHoverColors: ["#4FC3F7", "#0288D1", "#FFA500"],
    stripe: "#0099FF",
  },
};

// SVG stripe pattern generator
function makeStripePattern(color: string) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8">
      <rect width="8" height="8" fill="transparent"/>
      <line x1="0" y1="8" x2="8" y2="0" stroke="${color}" stroke-width="2"/>
    </svg>`;
  return {
    image: `data:image/svg+xml,${encodeURIComponent(svg)}`,
    repeat: "repeat",
  };
}

const ChartRenderer: React.FC<{
  data: ChartData;
  showBg?: boolean;
  colorScheme?: ColorScheme;
  showTitle?: boolean;
  style?: React.CSSProperties;
}> = ({
  data,
  showBg = true,
  colorScheme = "purple",
  showTitle = true,
  style,
}) => {
  const { title, type, x, y } = data;
  // Color scheme & helpers
  const clr = schemeColors[colorScheme];
  const stripePattern = useMemo(() => makeStripePattern(clr.stripe), [
    clr.stripe,
  ]);

  // ECharts ref
  const chartRef = useRef<ReactECharts>(null);

  // Y-axis name sync with legend selection
  const defaultAxisName = data.y?.[0]?.label ?? "";
  const [yAxisName, setYAxisName] = useState(defaultAxisName);
  useEffect(() => setYAxisName(defaultAxisName), [defaultAxisName]);

  // GA: fire once per hover (per title)
  const sentHoverRef = useRef(false);
  useEffect(() => {
    // reset when title changes (new chart)
    sentHoverRef.current = false;
  }, [title]);

  const handleFirstHover = useCallback(() => {
    if (!sentHoverRef.current) {
      gaService.chartInteraction(title);
      sentHoverRef.current = true;
    }
  }, [title]);

  // Stable legend handler; lazy update to avoid full redraw
  const isPieLike = ["pie", "donut", "radial"].includes(type);
  const onLegendSelectChanged = useCallback(
    (params: any) => {
      const chartInstance = chartRef.current?.getEchartsInstance();
      if (!chartInstance) return;

      // params.selected is a map of name -> boolean
      const visible = Object.entries(params.selected)
        .filter(([, on]) => on)
        .map(([name]) => name);

      // PIE / DONUT / RADIAL behavior
      if (isPieLike) {
        // If exactly 1 legend item selected, highlight it; otherwise downplay all
        const seriesIndex = 0; // your pie is at series[0]
        const allNames = Object.keys(params.selected);

        // if nothing visible, downplay all (or keep as-is)
        if (visible.length === 1) {
          const target = visible[0];
          // highlight target, downplay others
          chartInstance.dispatchAction({ type: "downplay", seriesIndex });
          chartInstance.dispatchAction({
            type: "highlight",
            seriesIndex,
            name: target,
          });
          // Optionally, you might want to set an external label somewhere:
          // setYAxisName(target); // if you still display a selected-name label in UI
        } else {
          // multiple or none selected -> remove highlights
          chartInstance.dispatchAction({ type: "downplay", seriesIndex });
          // If you want single-mode legend instead, set legend.selectedMode = 'single' in option
        }

        // return early since there's no yAxis to update
        return;
      }

      // NON-PIE behavior (line/bar/category/heatmap)
      const newName = visible.length === 1 ? visible[0] : defaultAxisName;
      setYAxisName(newName);

      // update only the yAxis name (lazyUpdate to avoid full re-render)
      chartInstance.setOption(
        { yAxis: { name: newName } },
        false /* notMerge */,
        true /* lazyUpdate */
      );
    },
    [defaultAxisName, isPieLike]
  );
  const events = useMemo(
    () => ({
      legendselectchanged: onLegendSelectChanged,
    }),
    [onLegendSelectChanged]
  );

  // ------- Common axis builders (pure) -------
  const buildCommonValueAxis = useCallback(
    (name?: string, useRangeFormat = false): echarts.EChartsOption["yAxis"] => {
      const shownRanges = new Set<string>();
      return {
        type: "value",
        splitLine: { lineStyle: { color: "#333", type: "dashed" } },
        axisLine: { show: false },
        axisLabel: {
          color: "#AAA",
          formatter: (v: number) => {
            if (!useRangeFormat) return abbreviateNumber(v);
            const range = toRange(v);
            if (shownRanges.has(range)) return "";
            shownRanges.add(range);
            return range;
          },
        },
        name,
        nameLocation: "middle",
        nameTextStyle: { fontWeight: "bold", color: "white" },
        nameGap: useRangeFormat ? 60 : 30,
      };
    },
    []
  );
  const isMobile = useDevice();
  const buildCommonCategoryAxis = useCallback(
    (axisLabel?: string, labels?: string[]) => ({
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: "#444" } },
      axisLabel: {
        color: "#AAA",
        interval: 0,
        rotate: (labels?.length || 0) > (isMobile ? 4 : 5) ? 45 : 0,
        align: (labels?.length || 0) > (isMobile ? 4 : 5) ? "right" : "center",
        formatter(value: string) {
          return (labels?.length || 0) > (isMobile ? 5 : 5)
            ? value.slice(0, 10) + "..."
            : value;
        },
        margin: (labels?.length || 0) > 5 ? 10 : 10,
      },
      name: axisLabel,
      nameLocation: "middle",
      nameTextStyle: { fontWeight: "bold", color: "white" },
    }),
    []
  );

  // ------- Memoized base option -------
  const base = useMemo<echarts.EChartsOption>(
    () => ({
      backgroundColor: "transparent",
      tooltip: {
        trigger: ["pie", "donut", "heatmap", "radial"].includes(type)
          ? "item"
          : "axis",
        backgroundColor: showBg ? "rgba(0,0,0,0.7)" : "transparent",
        textStyle: { color: "#fff" },
        extraCssText: `backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);`,
        formatter: (params: any) => {
          const paramsArr = Array.isArray(params) ? params : [params];
          const xLabel =
            paramsArr[0]?.axisValueLabel ?? paramsArr[0]?.name ?? "";
          const lines = paramsArr.map((p: any) => {
            const yAxis = y?.[p.seriesIndex ?? 0];
            const displayVal = yAxis?.display_values?.[p.dataIndex];
            const val =
              displayVal != null ? displayVal : abbreviateNumber(p.value);
            return `${p.marker ?? ""}${p.seriesName ?? p.name}: <strong>${val}</strong>`;
          });
          return `${xLabel}<br/>${lines.join("<br/>")}`;
        },
      },
      legend: {
        show: [
          "pie",
          "donut",
          "heatmap",
          "radial",
          "bar",
          "line",
          "bar-horizontal",
          "bar-stacked",
        ].includes(type),
        textStyle: { color: "#AAA" },
        bottom: type === "pie" || type === "donut" ? "0%" : undefined,
      },
    }),
    [type, showBg, y]
  );

  // ------- Memoized full option -------
  const option = useMemo<echarts.EChartsOption>(() => {
    switch (type) {
      case "line":
        return {
          ...base,
          xAxis: {
            ...(buildCommonCategoryAxis(x?.label, x?.values) as any),
            boundaryGap: false,
            nameGap: (x?.values?.length || 0) > (isMobile ? 5 : 6) ? 40 : 30,
          },
          yAxis: buildCommonValueAxis(
            yAxisName || y?.[0]?.label,
            y?.some((s) => s.display_values != null),
          ),
          series: (Array.isArray(y) ? y: []).map((s, i) => ({
            name: s.label,
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: clr.lineColors[i % clr.lineColors.length] },
                {
                  offset: 1,
                  color: clr.lineColors[(i + 1) % clr.lineColors.length],
                },
              ]),
            },
            areaStyle: {
              opacity: 0.3,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: clr.areaColors[0] },
                { offset: 1, color: clr.areaColors[1] },
              ]),
            },
            data: s.values,
          })),
        };

      case "bar":
        return {
          ...base,
          xAxis: {
            ...(buildCommonCategoryAxis(x?.label, x?.values) as any),
            nameGap: (x?.values?.length || 0) > (isMobile ? 4 : 5) ? 65 : 30,
          },
          yAxis: buildCommonValueAxis(
            yAxisName || y?.[0]?.label,
            y?.some((s) => s.display_values != null),
          ),
          series: (Array.isArray(y) ? y: []).map((s, index) => ({
            name: s.label,
            type: "bar",
            barWidth: 12,
            barMaxWidth: 12,
            itemStyle: {
              borderRadius: 86,
              color: clr.barColors[index % clr.barColors.length],
            },
            emphasis: {
              focus: "self",
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                  {
                    offset: 0,
                    color: clr.barColors[index % clr.barColors.length],
                  },
                  {
                    offset: 1,
                    color: clr.barColors[index % clr.barColors.length],
                  },
                ]),
              },
            },
            data: s.values,
          })),
        };

      case "category": {
        // sort categories by descending value
        const cats = x?.values ?? [];
        const vals = y?.[0]?.values ?? [];
        const sorted = cats
          .map((label, i) => ({ label, value: vals[i] ?? 0 }))
          .sort((a, b) => b.value - a.value);

        return {
          ...base,
          grid: {
            left: 100,
            right: 20,
            top: 20,
            bottom: 20,
            containLabel: true,
          },
          xAxis: {
            type: "value",
            splitLine: { lineStyle: { color: "#333", type: "dashed" } },
            axisLine: { show: false },
            axisLabel: {
              color: "#AAA",
              formatter: (v: number) => abbreviateNumber(v),
            },
            name: yAxisName || y?.[0]?.label,
            nameLocation: "middle",
            nameTextStyle: { fontWeight: "bold", color: "white" },
            nameGap: 30,
          },
          yAxis: {
            type: "category",
            data: sorted.map((d) => d.label),
            axisLine: { lineStyle: { color: "#444" } },
            axisLabel: { color: "#AAA" },
          },
          series: [
            {
              type: "bar",
              barWidth: 12,
              barMaxWidth: 14,
              itemStyle: {
                borderRadius: [0, 8, 8, 0],
                color: clr.barColors[0],
              },
              data: sorted.map((d) => d.value),
            },
          ],
        };
      }

      case "bar-stacked":
        return {
          ...base,
          xAxis: {
            ...(buildCommonCategoryAxis(x?.label, x?.values) as any),
            nameGap: 70,
          },
          yAxis: buildCommonValueAxis(
            yAxisName || y?.[0]?.label,
            y?.some((s) => s.display_values != null),
          ),
          series: (Array.isArray(y) ? y: []).map((s, index) => ({
            name: s.label,
            type: "bar",
            stack: "total",
            barWidth: 12,
            barMaxWidth: 12,
            itemStyle: {
              borderRadius: 4,
              color: clr.barColors[index % clr.barColors.length],
            },
            data: s.values,
          })),
        };

      case "donut":
      case "pie":
      case "radial": {
        const cats = x?.values ?? [];
        const vals = y?.[0]?.values ?? [];
        return {
          ...base,
          series: [
            {
              name: title,
              type: "pie",
              radius: type === "pie"? ["40%", "60%"] : "60%",
              center:["50%", "35%"],
              avoidLabelOverlap: false,
              label: { show: false },
              // labelLine: { show: false },
              data:
                type === "pie"
                  ? cats.map((n, i) => ({ name: n, value: vals[i] ?? 0 }))
                  : cats
                      .map((n, i) => ({ name: n, value: vals[i] ?? 0 }))
                      .sort(function (a, b) {
                        return a.value - b.value;
                      }),
              color: clr.pieColors,
              // roseType:type=== "pie" ? undefined :  "radius",
              labelLine: {
                lineStyle: {
                  color: "rgba(255, 255, 255, 0.3)",
                },
                smooth: 0.2,
                length: 10,
                length2: 20,
              },
              padAngle:type === "pie" ?  5 : 0,
              itemStyle:
                type === "pie"
                  ? {
                      borderRadius: 20,
                      // borderColor: "#000",
                      padding: 4,
                      borderWidth: 8,
                    }
                  : {
                      // color: '#ff0000',
                      borderRadius: 4,
                      shadowBlur: 200,
                      shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
            },
          ],
        };
      }

      case "heatmap": {
        const flat = y?.flatMap((s) => s.values) ?? [];
        const max = Math.max(...flat, 0);
        return {
          ...base,
          legend: {
            show: false,
          },
          xAxis: {
            type: "category",
            data: x?.values,
            axisLine: { lineStyle: { color: "#444" } },
            axisLabel: { color: "#AAA" },
          },
          yAxis: {
            type: "category",
            data: y?.map((s) => s.label),
            axisLine: { lineStyle: { color: "#444" } },
            axisLabel: { color: "#AAA" },
          },
          visualMap: {
            show: true,
            top: "top",
            left: "center",
            type: "piecewise",
            dimension: 2,
            orient: "horizontal",
            textGap: 10,
            textStyle: {
              color: "white",
            },
            pieces: [
              { min: 0, max: 40, color: "#25D07C40" },
              { min: 41, max: 60, color: "#25D07C60" },
              { min: 61, max: 80, color: "#25D07C80" },
              { min: 81, max: max, color: "#25D07C" },
            ],
          },
          series: [
            {
              name: title,
              type: "heatmap",
              data: (x?.values ?? [])
                .map((_, xi) =>
                  (Array.isArray(y) ? y: []).map((s, yi) => [xi, yi, s.values[xi]])
                )
                .flat() as number[][],
              itemStyle: { borderColor: "#08081A", borderWidth: 2 },
              emphasis: { itemStyle: { borderColor: "#fff", borderWidth: 1 } },
            },
          ],
        };
      }

      default:
        return base;
    }
  }, [
    type,
    base,
    buildCommonCategoryAxis,
    x?.label,
    x?.values,
    buildCommonValueAxis,
    yAxisName,
    y,
    clr.lineColors,
    clr.areaColors,
    clr.barColors,
    clr.pieColors,
    title,
    stripePattern,
  ]);
  function capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }
  if (data.type === "table") {
    const tableData = data.values;
    const columns =
      tableData && tableData.length > 0 ? Object.keys(tableData[0]) : [];
    return (
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          backgroundImage: showBg
            ? `url('https://sr-cdn-1.shiprocket.in/sr-promise/images/graph_chat_bg.svg')`
            : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          mt: 3,
          mb: 3,
        }}
      >
        {showTitle && (
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="body1" sx={{ color: "#fff", fontWeight: 700 }}>
              {data.title}
            </Typography>
          </Box>
        )}
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "transparent",
            maxHeight: "290px",
            overflowY: "auto",
            backgroundImage : "none",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ color: "#AAA", fontWeight: 600 }}>
                    {capitalizeFirstLetter(col)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData?.map((row: unknown, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ color: "#fff" }}>
                      {(row as Record<string, string>)[col]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: showBg ? "#08081A" : "transparent",
        borderRadius: "8px",
        width: "100%",
        overflow: "visible",
        backgroundImage: showBg
          ? `url('https://sr-cdn-1.shiprocket.in/sr-promise/images/graph_chat_bg.svg')`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {showTitle && (
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#fff", fontWeight: 500 }}
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={{ pb: 2 }} onMouseEnter={handleFirstHover}>
        <ReactECharts
          ref={chartRef}
          onEvents={events}
          option={{
            ...option,
            backgroundColor: "transparent",
            grid: {
              containLabel: true,
              top: 20,
              bottom: 20,
              left: 20,
              right: 20,
            },
          }}
          style={{
            width: "100%",
            height: isPieLike ? 200 : 300,
            touchAction: "pan-y pinch-zoom",
            ...style,
          }}
          opts={{ renderer: "canvas" }}
          notMerge={false}
          lazyUpdate={true}
        />
      </Box>
    </Box>
  );
};

export default memo(ChartRenderer);

// -------------- utils --------------
function toRange(n: number): string {
  if (n < 100) return "< 100";
  if (n < 500) return "100 - 500";
  if (n < 1000) return "500 - 1K";
  if (n < 5000) return "1K - 5K";
  if (n < 10000) return "5K - 10K";
  if (n < 20000) return "10K - 20K";
  if (n < 50000) return "20K - 50K";
  if (n < 100000) return "50K - 1L";
  if (n < 500000) return "1L - 5L";
  if (n < 1000000) return "5L - 10L";
  if (n < 5000000) return "10L - 50L";
  if (n < 10000000) return "50L - 1Cr";
  if (n < 20000000) return "1Cr - 2Cr";
  if (n < 30000000) return "2Cr - 3Cr";
  if (n < 40000000) return "3Cr - 4Cr";
  if (n < 50000000) return "4Cr - 5Cr";
  if (n < 60000000) return "5Cr - 6Cr";
  if (n < 70000000) return "6Cr - 7Cr";
  if (n < 80000000) return "7Cr - 8Cr";
  if (n < 90000000) return "8Cr - 9Cr";
  if (n < 100000000) return "9Cr - 10Cr";
  return "10Cr+";
}

function abbreviateNumber(num: number) {
  const abs = Math.abs(num);
  const units = [
    { value: 1e7, symbol: "Cr" }, // crore
    { value: 1e5, symbol: "L" }, // lakh
    { value: 1e3, symbol: "k" }, // thousand
  ];
  for (const { value, symbol } of units) {
    if (abs >= value) {
      const divided = num / value;
      const str =
        Math.floor(divided) !== divided
          ? divided.toFixed(1)
          : divided.toString();
      return str + symbol;
    }
  }
  return num.toString();
}
